import {
  Banknote,
  Calendar,
  CalendarCheck,
  Coins,
  Minus,
  StickyNote,
  TrendingDown,
  TrendingUp,
  User,
  UserCheck,
  Wallet,
} from "lucide-react";
import { RegisterSession } from "@/redux/services/pos/pos.type";
import {
  displayPrice,
  formatDate,
  formatVariance,
  getSessionStatusLabel,
} from "@/lib/utils";

interface SessionSummaryProps {
  session: RegisterSession;
  currency: string;
}

interface MetricProps {
  icon: React.ComponentType<{ className?: string }>;
  iconClassName: string;
  label: string;
  value: React.ReactNode;
  hint?: string;
}

const Metric = ({ icon: Icon, iconClassName, label, value, hint }: MetricProps) => (
  <div className="flex items-start gap-3">
    <div
      className={`flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${iconClassName}`}
    >
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-base font-semibold tabular-nums">{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  </div>
);

const SessionSummary = ({ session, currency }: SessionSummaryProps) => {
  const variance = session.variance;
  const hasVariance = variance != null;
  const isExcess = hasVariance && variance > 0;
  const isShort = hasVariance && variance < 0;

  const varianceTone = isExcess
    ? "bg-success/10 text-success border-success/20"
    : isShort
    ? "bg-destructive/10 text-destructive border-destructive/20"
    : "bg-muted text-muted-foreground border-border";

  const VarianceIcon = isExcess
    ? TrendingUp
    : isShort
    ? TrendingDown
    : Minus;

  const varianceLabel = isExcess
    ? "Excédent"
    : isShort
    ? "Déficit"
    : "Aucun écart";

  return (
    <div className="dash__card p-6 space-y-6">
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
            session.status === "open"
              ? "bg-green-100 text-green-700"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {getSessionStatusLabel(session.status)}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Metric
          icon={Calendar}
          iconClassName="bg-primary/10 text-primary"
          label="Ouverte le"
          value={formatDate(new Date(session.openedAt))}
        />
        <Metric
          icon={User}
          iconClassName="bg-primary/10 text-primary"
          label="Ouverte par"
          value={session.openedBy?.fullName ?? "—"}
        />
        {session.closedAt && (
          <Metric
            icon={CalendarCheck}
            iconClassName="bg-warning/10 text-warning"
            label="Fermée le"
            value={formatDate(new Date(session.closedAt))}
          />
        )}
        {session.closedBy && (
          <Metric
            icon={UserCheck}
            iconClassName="bg-warning/10 text-warning"
            label="Fermée par"
            value={session.closedBy.fullName ?? "—"}
          />
        )}
      </div>

      <hr className="border-border" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Metric
          icon={Wallet}
          iconClassName="bg-muted text-muted-foreground"
          label="Fond de caisse"
          value={displayPrice(session.openingBalance, currency)}
        />
        <Metric
          icon={Banknote}
          iconClassName="bg-success/10 text-success"
          label="Espèces attendues"
          value={
            session.expectedCash != null
              ? displayPrice(session.expectedCash, currency)
              : "—"
          }
        />
        <Metric
          icon={Coins}
          iconClassName="bg-success/10 text-success"
          label="Espèces comptées"
          value={
            session.actualCash != null
              ? displayPrice(session.actualCash, currency)
              : "—"
          }
        />
      </div>

      {hasVariance && (
        <div
          className={`rounded-lg border p-4 flex items-center gap-4 ${varianceTone}`}
        >
          <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-white/50 flex items-center justify-center">
            <VarianceIcon className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">{varianceLabel}</p>
            <p className="text-xl font-bold tabular-nums">
              {formatVariance(variance / 100, currency)}
            </p>
          </div>
        </div>
      )}

      {session.notes && (
        <>
          <hr className="border-border" />
          <Metric
            icon={StickyNote}
            iconClassName="bg-muted text-muted-foreground"
            label="Notes"
            value={session.notes}
          />
        </>
      )}
    </div>
  );
};

export default SessionSummary;
